<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesPartyPhoneField from './SalesPartyPhoneField.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  partyRole: { type: String, default: 'comprador' },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const attorney = computed(() => props.profile.attorney || {});
const showWarning = ref(false);
const labels = computed(() =>
  props.partyRole === 'vendedor'
    ? {
        enabled: t('SALES_PARTIES.ATTORNEY.ENABLED_SELLER'),
        title: t('SALES_PARTIES.ATTORNEY.TITLE_SELLER'),
      }
    : {
        enabled: t('SALES_PARTIES.ATTORNEY.ENABLED_BUYER'),
        title: t('SALES_PARTIES.ATTORNEY.TITLE_BUYER'),
      }
);
const update = (field, value) => emit('update', 'attorney', field, value);
const toggle = enabled => {
  showWarning.value = true;
  update('enabled', enabled);
  if (!enabled) {
    update('name', '');
    update('country_code', '');
    update('phone', '');
  }
};
const attorneyName = computed(() => String(attorney.value.name || ''));
const nameInputClasses = computed(() => {
  if (props.errors.name) {
    return 'border-[#D64545] text-[#D64545]';
  }
  if (attorneyName.value) {
    return 'border-[#009F94] font-bold text-[#009F94]';
  }
  return 'border-[#A7B7BE] text-[#7F8C93]';
});
</script>

<template>
  <section
    data-test="sales-party-attorney-section"
    class="rounded-[10px] bg-white p-8 font-roboto shadow-sm max-[700px]:p-0"
  >
    <div class="grid w-full" :data-party-role="partyRole">
      <header>
        <h2
          data-test="sales-party-attorney-title"
          class="max-w-[832px] font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:max-w-[326px] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
        >
          {{ labels.title }}
        </h2>
      </header>

      <button
        type="button"
        data-test="sales-party-attorney-toggle"
        :aria-pressed="Boolean(attorney.enabled)"
        class="mt-9 flex w-full max-w-[400px] items-center gap-3 overflow-hidden whitespace-nowrap rounded-[10px] bg-[#EDF8F7] px-3 py-1.5 text-base leading-6 text-[#009F94]"
        @click="toggle(!attorney.enabled)"
      >
        <span
          class="relative inline-flex h-3 w-9 shrink-0 items-center rounded-full bg-[#8AD8D1]"
          aria-hidden="true"
        >
          <span
            class="absolute left-0 size-5 rounded-full bg-[#009F94] shadow-sm transition-transform"
            :class="attorney.enabled ? 'translate-x-4' : 'translate-x-0'"
          />
        </span>
        <span>{{ labels.enabled }}</span>
      </button>

      <div
        v-if="attorney.enabled"
        data-test="sales-party-attorney-fields"
        class="mt-[51px] grid gap-8 min-[701px]:grid-cols-2 min-[701px]:gap-4"
      >
        <SalesPropertyReferenceField
          test-id="sales-party-attorney-name"
          :label="`${t('SALES_PARTIES.ATTORNEY.NAME')}*`"
          :filled="Boolean(attorneyName)"
          :error="errors.name"
        >
          <input
            :value="attorneyName"
            :placeholder="t('SALES_PARTIES.ATTORNEY.NAME_PLACEHOLDER')"
            class="reset-base h-8 w-full border-0 border-b bg-transparent px-0 pr-7 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0"
            :class="nameInputClasses"
            @input="update('name', $event.target.value)"
          />
        </SalesPropertyReferenceField>
        <SalesPartyPhoneField
          :label="t('SALES_PARTIES.FORM.PHONE')"
          :country-code="attorney.country_code"
          :phone="attorney.phone"
          :error="errors.phone"
          @update="update"
        />
      </div>
      <div
        v-if="attorney.enabled && showWarning"
        data-test="sales-party-attorney-warning"
        class="mt-8 flex items-start gap-3 rounded-[10px] bg-[#FFF5D8] px-4 py-3 text-sm leading-5 text-[#5D696F]"
      >
        <span
          class="i-lucide-circle-alert mt-0.5 shrink-0 text-[#CC8E00]"
          aria-hidden="true"
        />
        <p class="flex-1">
          {{ t('SALES_PARTIES.ATTORNEY.WARNING_PREFIX') }}
          <strong>{{ t('SALES_PARTIES.ATTORNEY.WARNING_HIGHLIGHT') }}</strong>
          {{ t('SALES_PARTIES.ATTORNEY.WARNING_SUFFIX') }}
        </p>
        <button
          type="button"
          class="i-lucide-x mt-0.5 size-4 shrink-0 text-[#5D696F]"
          :aria-label="t('SALES_PARTIES.COMMON.CLOSE_NOTICE')"
          @click="showWarning = false"
        />
      </div>
    </div>
  </section>
</template>
